import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import {
  type ApiToken,
  useApiTokens,
  useMintApiToken,
  useRevokeApiToken,
} from '@/lib/apiTokens';

interface Props {
  orgSlug: string;
}

/**
 * Per-user bearer-token management. Lives on the org settings page
 * because that's where every other credential surface (distributor
 * keys, SMTP, KiCad sync) is — but tokens are user-scoped, not
 * org-scoped, so a token minted here can read any org you're a
 * member of.
 *
 * Mint flow follows the reveal-once pattern: the plaintext is
 * shown exactly once in a code box with a copy button, then the
 * panel returns to its list view and only the hash stays on the
 * server. There's no way to recover the plaintext later — if it's
 * lost, mint a new one and revoke the old.
 */
export function ApiTokensPanel({ orgSlug }: Props) {
  const list = useApiTokens(orgSlug);
  const mint = useMintApiToken(orgSlug);
  const revoke = useRevokeApiToken(orgSlug);

  const [name, setName] = useState('');
  // Set after a successful mint; cleared when the user dismisses the
  // reveal panel. Holds the plaintext + the row's metadata so we
  // can show "Created token: <name>" alongside.
  const [freshlyMinted, setFreshlyMinted] = useState<{
    token: string;
    name: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const onMint = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) return;
    const result = await mint.mutateAsync({ name: name.trim() });
    setFreshlyMinted({ token: result.token, name: result.record.name });
    setName('');
    setCopied(false);
  };

  const onCopy = async () => {
    if (!freshlyMinted) return;
    try {
      await navigator.clipboard.writeText(freshlyMinted.token);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    } catch {
      /* clipboard may be blocked over plain HTTP; user can manual-select */
    }
  };

  const tokens = list.data ?? [];
  const active = tokens.filter((t) => !t.revoked_at);
  const revoked = tokens.filter((t) => t.revoked_at);

  return (
    <section className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
      <div className="border-b border-stone-200 dark:border-stone-800 p-4">
        <h2 className="text-lg font-semibold">API tokens</h2>
        <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">
          Bearer tokens for external integrations — currently the
          KiCad sync plugin. Tokens are tied to your user account
          (not this org), so each token can read any org you belong
          to. Plaintext is shown once at creation; only the hash is
          stored.
        </p>
      </div>

      {freshlyMinted && (
        <div className="border-b border-stone-200 dark:border-stone-800 bg-amber-50 dark:bg-amber-900/20 p-4">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            Token created: <span className="font-mono">{freshlyMinted.name}</span>
          </p>
          <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
            Copy this now — it won't be shown again. If you lose it,
            revoke it and mint a new one.
          </p>
          <div className="mt-3 flex items-stretch gap-2">
            <code className="flex-1 select-all overflow-x-auto rounded border border-amber-300 dark:border-amber-700 bg-white dark:bg-stone-950 px-3 py-2 text-xs font-mono break-all">
              {freshlyMinted.token}
            </code>
            <Button onClick={onCopy} variant="secondary">
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setFreshlyMinted(null)}
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      <form onSubmit={onMint} className="border-b border-stone-200 dark:border-stone-800 p-4">
        <label className="block text-sm font-medium mb-1">New token</label>
        <div className="flex items-center gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. windows-laptop"
            required
            className="flex-1"
          />
          <Button type="submit" disabled={mint.isPending || !name.trim()}>
            {mint.isPending ? 'Creating…' : 'Create token'}
          </Button>
        </div>
        {mint.isError && (
          <p className="mt-2 text-sm text-red-600">
            {(mint.error as Error).message}
          </p>
        )}
      </form>

      <div className="p-2">
        {list.isLoading && (
          <div className="flex items-center gap-2 p-3 text-sm text-stone-500">
            <Spinner /> Loading…
          </div>
        )}
        {list.isError && (
          <p className="p-3 text-sm text-red-600">
            Couldn't load tokens: {(list.error as Error).message}
          </p>
        )}
        {!list.isLoading && tokens.length === 0 && (
          <p className="p-3 text-sm text-stone-500 dark:text-stone-400">
            No tokens yet. Create one above to use the KiCad sync plugin.
          </p>
        )}
        {active.length > 0 && (
          <ul className="divide-y divide-stone-100 dark:divide-stone-800">
            {active.map((t) => (
              <TokenRow
                key={t.id}
                token={t}
                onRevoke={() => revoke.mutate(t.id)}
                revoking={revoke.isPending && revoke.variables === t.id}
              />
            ))}
          </ul>
        )}
        {revoked.length > 0 && (
          <details className="mt-2 border-t border-stone-100 dark:border-stone-800 pt-2">
            <summary className="cursor-pointer px-3 py-2 text-xs text-stone-500 dark:text-stone-400">
              Revoked tokens ({revoked.length})
            </summary>
            <ul className="divide-y divide-stone-100 dark:divide-stone-800 opacity-60">
              {revoked.map((t) => (
                <TokenRow key={t.id} token={t} />
              ))}
            </ul>
          </details>
        )}
      </div>
    </section>
  );
}

function TokenRow({
  token,
  onRevoke,
  revoking,
}: {
  token: ApiToken;
  onRevoke?: () => void;
  revoking?: boolean;
}) {
  return (
    <li className="flex items-center gap-3 px-3 py-2">
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{token.name}</div>
        <div className="text-xs text-stone-500 dark:text-stone-400">
          Created {formatTs(token.created_at)}
          {token.last_used_at
            ? ` · Last used ${formatTs(token.last_used_at)}`
            : ' · Never used'}
          {token.revoked_at
            ? ` · Revoked ${formatTs(token.revoked_at)}`
            : ''}
        </div>
      </div>
      {!token.revoked_at && onRevoke && (
        <Button
          variant="secondary"
          onClick={() => {
            if (confirm(`Revoke token "${token.name}"? Any plugin using it will need a new token.`)) {
              onRevoke();
            }
          }}
          disabled={revoking}
        >
          {revoking ? 'Revoking…' : 'Revoke'}
        </Button>
      )}
    </li>
  );
}

function formatTs(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}
